import {
  CheckboxQuestionType,
  CheckboxRulesEnum,
  CheckboxRulesType,
  HourFormatEnum,
  HourQuestionType,
  NumberQuestionDetailsType,
  NumberQuestionType,
  QuestionTextTypeEnum,
  QuestionType,
  QuestionTypeEnum,
  TextQuestionType,
} from "../types/Question";
import {
  AnswerValueType,
  getSequenceAnswersAnswerType,
  PostAnswersBodyType,
} from "../types/Answer";
import { validateEmail } from "./string";

/* ------------------------- Defaults & answered checks ------------------------- */

export const defaultAnswerValueByType: Record<
  QuestionTypeEnum,
  AnswerValueType
> = {
  CHECKBOX: [],
  RADIOBUTTON: null,
  HOUR: "",
  NUMBER: null,
  DATE: "",
  SLIDER: null,
  DROPDOWN: null,
  TEXT: "",
  // Answerless: the seeded entry only exists so the flow engine (which requires a
  // currentAnswer entry to advance) has something to hold.
  INFORMATION: null,
};

const isNonEmptyString = (v: unknown) =>
  typeof v === "string" && v.trim() !== "";

const isNumberish = (v: unknown) => typeof v === "number" && !Number.isNaN(v);
/** NOTE: exported name kept as-is to avoid breaking imports elsewhere */
export const hasAsweredFunctionsByType: Record<
  QuestionTypeEnum,
  (answer: AnswerValueType) => boolean
> = {
  CHECKBOX: (answer) => Array.isArray(answer) && answer.length > 0,

  // ✅ consider number or non-empty string as answered; null/undefined/'' => not answered
  RADIOBUTTON: (answer) => isNumberish(answer) || isNonEmptyString(answer),
  DROPDOWN: (answer) => isNumberish(answer) || isNonEmptyString(answer),

  HOUR: (answer) => isNonEmptyString(answer),
  DATE: (answer) => isNonEmptyString(answer),
  TEXT: (answer) => isNonEmptyString(answer),

  // NUMBER answers arrive from the text input as a STRING (kept as a string while editing for
  // per-keystroke UX — see AnswerNumber). A numeric string must also count as answered, or a
  // MANDATORY number question can never enable "Next" (isNumberAnswerValid already parseFloats
  // the same value). SLIDER values are real numbers, so it keeps the finite-number check.
  NUMBER: (answer) =>
    isNumberish(answer as number) ||
    (isNonEmptyString(answer) && !Number.isNaN(parseFloat(answer as string))),
  SLIDER: (answer) => isNumberish(answer as number),

  // MUST stay false: true would let the endAfter/last-question heuristics treat the
  // pre-seeded entry as an answer and end questionnaires early. Validity is handled
  // by useAnswerRules' INFORMATION short-circuit (always valid), not by this flag.
  INFORMATION: () => false,
};

/* ------------------------------ Checkbox rules ------------------------------ */

const validitateByConstraintType: Record<
  CheckboxRulesEnum,
  (nbValues: number, quantity: number) => boolean
> = {
  NONE: () => true,
  EXACTLY: (nbValues, quantity) => nbValues === quantity,
  AT_MOST: (nbValues, quantity) => nbValues <= quantity,
  AT_LEAST: (nbValues, quantity) => nbValues >= quantity,
};

const isCheckboxAnswerValid = (
  values: number[] = [],
  rules: CheckboxRulesType,
): boolean => {
  const { constraint, quantity } = rules;
  return validitateByConstraintType[constraint](values.length, quantity);
};

const isRadioAnswerValid = (): boolean => true;

/* ---------------------------- Time utilities (fast) ---------------------------- */

/** Parse "HH:mm" into minutes since midnight. Returns null if invalid. */
function hhmmToMinutes(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return h * 60 + min;
}

/** Extract minutes since midnight from either "HH:mm" or any date string. */
function answerToMinutes(answer: string): number | null {
  // Try "HH:mm" first
  const direct = hhmmToMinutes(answer);
  if (direct !== null) return direct;

  // Fallback: parse as Date (handles ISO strings, etc.) using local time
  const d = new Date(answer);
  if (isNaN(d.getTime())) return null;
  return d.getHours() * 60 + d.getMinutes();
}

/**
 * Check if a time (in minutes) is within [min,max] inclusive for the same day,
 * supporting windows that cross midnight (e.g., 22:00..06:00).
 */
function isTimeWithinWindow(
  ansMin: number,
  minMin: number,
  maxMin: number,
): boolean {
  if (minMin <= maxMin) {
    // Normal window: [min, max]
    return ansMin >= minMin && ansMin <= maxMin;
  }
  // Cross-midnight window: [min..1440) U [0..max]
  return ansMin >= minMin || ansMin <= maxMin;
}

/* ------------------------------- Validations ------------------------------- */

const isHourAnswerValid = (
  question: HourQuestionType,
  answer: string,
): boolean => {
  const { min_hour, max_hour } = question.detail;

  const currentMin = answerToMinutes(answer);
  const minMin = hhmmToMinutes(min_hour);
  const maxMin = hhmmToMinutes(max_hour);

  if (currentMin === null || minMin === null || maxMin === null) return false;

  return isTimeWithinWindow(currentMin, minMin, maxMin);
};

const checkDecimalNumber = (value: number, decNumber: number): boolean => {
  const numberOfDecimals = value.toString().split(".")[1]?.length || 0;
  return numberOfDecimals <= decNumber;
};

const checkMinMax = (value: number, min: number, max: number): boolean =>
  value >= min && value <= max;

const isNumberAnswerValid = (
  svalue: string,
  details: NumberQuestionDetailsType,
): boolean => {
  const { number_decimal, min_value: min, max_value: max } = details;
  const value = parseFloat(svalue);
  return (
    !isNaN(value) &&
    checkDecimalNumber(value, number_decimal) &&
    checkMinMax(value, min, max)
  );
};

const isDateAnswerValid = (): boolean => true;
const isSliderAnswerValid = (): boolean => true;
const isDropdownAnswerValid = (): boolean => true;

// Identity phone question (identity_target = 'phone_number' on a NUMBER question): digits with an
// optional leading "+", 6-15 digits — mirrors the API-side check. Formatting noise the input may
// let through (spaces/dots/dashes from paste) is stripped before testing.
export const isPhoneAnswerValid = (value: string): boolean =>
  /^\+?\d{6,15}$/.test(String(value ?? "").replace(/[\s.-]/g, ""));

const validitateByTextType: Record<
  QuestionTextTypeEnum,
  (value: string) => boolean
> = {
  EMAIL: (value) => validateEmail(value),
  PARAGRAPH: () => true,
  LINE: () => true,
};

const isTextAnswerValid = (
  value: string = "",
  type: QuestionTextTypeEnum,
): boolean => validitateByTextType[type](value);

export const validationFunctionsByType: Record<
  QuestionTypeEnum,
  (question: QuestionType, answer: AnswerValueType) => boolean
> = {
  CHECKBOX: (question, answer) => {
    const {
      detail: { rules },
    } = question as CheckboxQuestionType;
    return isCheckboxAnswerValid(answer as number[], rules);
  },
  RADIOBUTTON: isRadioAnswerValid,
  HOUR: isHourAnswerValid,
  NUMBER: (question, answer) => {
    // Identity phone: the number rules (min/max/decimals) don't apply — phone format instead.
    if (question.identity_target === "phone_number") return isPhoneAnswerValid(answer as string);
    const { detail } = question as NumberQuestionType;
    return isNumberAnswerValid(answer as string, detail);
  },
  DATE: isDateAnswerValid,
  SLIDER: isSliderAnswerValid,
  DROPDOWN: isDropdownAnswerValid,
  TEXT: (question, answer) => {
    const {
      detail: { type },
    } = question as TextQuestionType;
    return isTextAnswerValid(answer as string, type);
  },
  // Nothing to validate on a full-page content screen.
  INFORMATION: () => true,
};

/* ---------------------------- Answer formatting ---------------------------- */

export function getAnswerValue(
  type: QuestionTypeEnum,
  answer: getSequenceAnswersAnswerType,
): AnswerValueType {
  switch (type) {
    case QuestionTypeEnum.CHECKBOX:
      return Array.isArray(answer.answer_id)
        ? answer.answer_id
        : [answer.answer_id].filter((x) => x != null);
    case QuestionTypeEnum.DROPDOWN:
    case QuestionTypeEnum.RADIOBUTTON: {
      const ids = Array.isArray(answer.answer_id)
        ? answer.answer_id
        : [answer.answer_id];
      const first = ids.find((x) => x != null);
      return first != null ? Number(first) : null; // <- return number|null
    }
    default:
      // ?? not ||: a stored 0 is falsy, and `||` restored it as null on resume — a
      // NUMBER answered 0 then came back "unanswered", which silently changes both
      // threshold routing ("<= 3" stops matching) and repeating-set expansion.
      return answer.value ?? defaultAnswerValueByType[type];
  }
}

export function formatAnswers(
  answers: getSequenceAnswersAnswerType[],
): getSequenceAnswersAnswerType[] {
  // Merge per (question_id, instance_index): multiple checkbox selections within the
  // same answer collapse into one answer_id array, while distinct follow-up-set
  // iterations of the same question stay separate (so they can be restored per instance).
  return answers.reduce((acc, curr) => {
    const foundindex = acc.findIndex(
      (ans) =>
        curr.question_id === ans.question_id &&
        (curr.instance_index ?? 0) === (ans.instance_index ?? 0),
    );
    if (foundindex > -1) {
      const existing = acc[foundindex];
      const existingIds = Array.isArray(existing.answer_id)
        ? existing.answer_id
        : [existing.answer_id];
      const currIds = Array.isArray(curr.answer_id)
        ? curr.answer_id
        : [curr.answer_id];
      acc[foundindex] = {
        ...existing,
        answer_id: [...existingIds, ...currIds],
      };
    } else {
      acc.push({
        ...curr,
        answer_id: Array.isArray(curr.answer_id)
          ? curr.answer_id.slice()
          : [curr.answer_id],
      });
    }
    return acc;
  }, [] as getSequenceAnswersAnswerType[]);
}

/* ------------------------------ Hour formatting ------------------------------ */

export function formatHour(hour: string, format: HourFormatEnum) {
  // Accepts "HH:mm" (preferred) or an ISO date-time string.
  const toHhMm = (s: string): { h: number; m: number } | null => {
    const direct = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
    if (direct) {
      const h = Number(direct[1]);
      const m = Number(direct[2]);
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) return { h, m };
      return null;
    }
    const d = new Date(s);
    if (isNaN(d.getTime())) return null;
    return { h: d.getHours(), m: d.getMinutes() };
  };

  const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n));

  switch (format) {
    case HourFormatEnum["12HOURS"]: {
      const hm = toHhMm(hour);
      if (!hm) return hour;
      const { h, m } = hm;
      const suffix = h >= 12 ? "PM" : "AM";
      const h12 = h % 12 === 0 ? 12 : h % 12;
      return `${pad2(h12)}:${pad2(m)} ${suffix}`;
    }
    case HourFormatEnum["24HOURS-AM/PM"]:
    case HourFormatEnum["24HOURS"]:
    default:
      return hour;
  }
}

export function formatAnswerForPost(
  question: QuestionType,
  val: AnswerValueType,
  questionnaireVersion: number,
  instanceIndex = 0,
): PostAnswersBodyType {
  const is_empty_answer = !hasAsweredFunctionsByType[question.type]?.(val);

  let answer_id: number[] = [];
  let value: any[] = [];

  switch (question.type) {
    case QuestionTypeEnum.CHECKBOX:
      answer_id = Array.isArray(val) ? val.map(Number) : [];
      value = Array.isArray(val) ? val : val != null ? [val] : [];
      break;

    case QuestionTypeEnum.RADIOBUTTON:
    case QuestionTypeEnum.DROPDOWN: {
      // ✅ Accept both string and number
      const numericVal =
        typeof val === "string" && !isNaN(Number(val))
          ? Number(val)
          : typeof val === "number"
            ? val
            : null;
      answer_id = numericVal !== null ? [numericVal] : [];
      value = []; // backend expects empty value array for dropdowns
      break;
    }

    case QuestionTypeEnum.SLIDER:
    case QuestionTypeEnum.NUMBER:
      value = Array.isArray(val) ? val : val != null ? [val] : [];
      break;

    case QuestionTypeEnum.TEXT:
    case QuestionTypeEnum.DATE:
    case QuestionTypeEnum.HOUR:
      answer_id = [];
      value = Array.isArray(val) ? val : val != null ? [val] : [];
      break;

    default:
      value = Array.isArray(val) ? val : val != null ? [val] : [];
  }

  return {
    question_type: question.type,
    questionnaire_version: questionnaireVersion,
    question_version: Number(question.version ?? 1),
    is_empty_answer,
    question_type_id: Number(question.detail.id),
    question_type_version: Number(question.detail.version),
    answer_id,
    answer_date: new Date().toISOString(),
    value,
    instance_index: instanceIndex,
  };
}
